import React, { ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Fade,
  Slide,
  Typography,
  Avatar,
} from '@mui/material';

interface FormStepProps {
  children: ReactNode;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'secondary';
  show?: boolean;
  direction?: 'left' | 'right' | 'up' | 'down';
  footer?: ReactNode;
  headerActions?: ReactNode;
}

const FormStep: React.FC<FormStepProps> = ({
  children,
  title,
  subtitle,
  icon,
  color = 'primary',
  show = true,
  direction = 'left',
  footer,
  headerActions
}) => {
  return (
    <Slide direction={direction} in={show} timeout={400} mountOnEnter unmountOnExit>
      <Fade in={show} timeout={600}>
        <Card
          sx={{
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            background: 'white',
            overflow: 'hidden',
            borderRadius: '0 !important',
            '& *': {
              borderRadius: '0 !important'
            },
            '&.MuiCard-root': {
              borderRadius: '0 !important'
            }
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: '#1F1F1F',
              p: 3,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 0
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              position: 'relative', 
              zIndex: 1 
            }}>
              {icon && (
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    mr: 2,
                    bgcolor: '#e72f0d',
                  }}
                >
                  {icon}
                </Avatar>
              )}
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600,
                    mb: subtitle ? 0.5 : 0
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.9,
                      fontSize: '0.875rem'
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            {headerActions && (
              <Box sx={{ position: 'absolute', right: 16, top: 16, zIndex: 2 }}>
                {headerActions}
              </Box>
            )}
          </Box>

          {/* Content */}
          <CardContent
            sx={{
              p: 4,
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
              '&:last-child': {
                pb: 4
              }
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {children}
            </Box>

            {footer && (
              <Box sx={{
                mt: 3,
                pt: 2,
                borderTop: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                {footer}
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Slide>
  );
};

const getColorGradient = (color: string): string => {
  switch (color) {
    case 'primary':
      return '#1976d2 0%, #1565c0 100%';
    case 'success':
      return '#388e3c 0%, #2e7d32 100%';
    case 'warning':
      return '#f57c00 0%, #ef6c00 100%';
    case 'error':
      return '#d32f2f 0%, #c62828 100%';
    case 'secondary':
      return '#9c27b0 0%, #8e24aa 100%';
    default:
      return '#1976d2 0%, #1565c0 100%';
  }
};

export default FormStep;
